import { cn } from '@/lib/utils'
import { useMedia } from '@/utils/hooks'
import { format as frmt } from '@/utils/date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addYears, endOfYear } from 'date-fns'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import type { CalendarWeek, DateRange } from 'react-day-picker'
import type { FAIconProps } from '../fa-icon'
import { FAIcon } from '../fa-icon'
import type { CalendarProps } from '../ui'
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui'
import { QuickRanges } from './quick-ranges'
import { getAlternatingRangeValue } from './util'

interface CommonProps {
  id?: string
  className?: string
  triggerClassName?: string

  placeholder?: string
  triggerIcon?: FAIconProps
  disabled?: boolean
  clearable?: boolean
  format?: string
  hideWeekNumbers?: boolean
  disabledDates?: CalendarProps['disabled']
  footer?: ReactNode
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: Date | null
  selectionMode?: never

  disableWeekClick?: false
  hideQuickRanges?: false

  onChange(date: Date | null): void
}

type RangeSelectionMode = 'alternating' | 'continuous'

interface RangeProps extends CommonProps {
  mode: 'range'
  value: DateRange | null
  selectionMode?: RangeSelectionMode

  disableWeekClick?: boolean
  hideQuickRanges?: boolean

  onChange(dates: DateRange | null): void
}

type Props = SingleProps | RangeProps

export function DatePicker({
  id,
  className,
  triggerClassName,

  mode,
  value,
  selectionMode = 'alternating',

  placeholder,
  triggerIcon,
  disabled = false,
  clearable = false,
  format = 'PPP',
  hideWeekNumbers = false,
  disableWeekClick = false,
  hideQuickRanges = false,
  disabledDates,
  footer,

  onChange,
}: Props) {
  const translations = useTranslate({
    placeholder: 'common.select-date',
    clear: 'components.date-picker.clear',
  })

  const [open, setOpen] = useState(false)
  const nextRangeBoundaryRef = useRef<'from' | 'to'>('from')
  const [mobile] = useMedia()

  const label = (() => {
    if (!value) return placeholder ?? translations.placeholder

    if (mode === 'single') return frmt(value, format)

    const { from, to } = value
    if (!from) return placeholder ?? translations.placeholder

    return `${frmt(from, format)} - ${to ? frmt(to, format) : '?'}`
  })()

  function handleWeekClick(week: CalendarWeek) {
    if (disableWeekClick || mode === 'single') return

    const firstDay = week.days.at(0)
    const lastDay = week.days.at(-1)

    if (!firstDay || !lastDay) return
    const firstDate = firstDay.date
    const lastDate = lastDay.date

    onChange({ from: firstDate, to: lastDate })

    if (selectionMode === 'alternating') {
      nextRangeBoundaryRef.current = 'from'
    }
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              'w-full min-w-0 justify-between px-4 bg-quaternary border-gray-200',
              'disabled:opacity-100',
              open && 'border-secondary hover:border-secondary',
              triggerClassName
            )}
            disabled={disabled}
          >
            <span className="min-w-0 flex-1 truncate text-left">{label}</span>

            <div className="flex items-center gap-2 pl-4">
              {clearable && !!value && !disabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation()
                    if (mode === 'range' && selectionMode === 'alternating') {
                      nextRangeBoundaryRef.current = 'from'
                    }
                    onChange(null)
                  }}
                >
                  {translations.clear}
                </Button>
              )}

              {(!!triggerIcon || !disabled) && (
                <FAIcon
                  icon={open ? 'chevron-up' : 'chevron-down'}
                  {...triggerIcon}
                />
              )}
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-hidden p-0 md:w-auto md:max-w-none"
          align="start"
        >
          {mode === 'single' ? (
            <Calendar
              mode="single"
              captionLayout="label"
              selected={value ?? undefined}
              defaultMonth={value ?? undefined}
              startMonth={new Date(1900, 0, 1)}
              endMonth={endOfYear(addYears(new Date(), 5))}
              showWeekNumber={!hideWeekNumbers}
              disabled={disabledDates}
              footer={footer}
              onSelect={value => {
                onChange(
                  !value
                    ? null
                    : new Date(
                        value.getFullYear(),
                        value.getMonth(),
                        value.getDate(),
                        12, // Try to avoid timezone issues with pure dates
                        0
                      )
                )
                setOpen(false)
              }}
            />
          ) : (
            <div className="flex flex-col md:flex-row">
              {!hideQuickRanges && (
                <QuickRanges value={value} onClick={onChange} />
              )}

              <Calendar
                mode="range"
                captionLayout="label"
                selected={value ?? undefined}
                defaultMonth={value?.from}
                numberOfMonths={mobile ? 1 : 2}
                startMonth={new Date(2000, 0, 1)}
                endMonth={endOfYear(addYears(new Date(), 5))}
                showWeekNumber={!hideWeekNumbers}
                disabled={disabledDates}
                footer={footer}
                onWeekClick={handleWeekClick}
                onSelect={(selected, triggerDate, modifiers) => {
                  if (selectionMode === 'continuous') {
                    onChange(selected ?? null)
                    return
                  }

                  if (modifiers.disabled) return

                  const boundary = !value?.from
                    ? 'from'
                    : !value.to
                    ? 'to'
                    : nextRangeBoundaryRef.current

                  const { range, nextBoundary } = getAlternatingRangeValue(
                    value,
                    triggerDate,
                    boundary
                  )
                  onChange(range)
                  nextRangeBoundaryRef.current = nextBoundary
                }}
              />
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
